import React, { useEffect, useState } from 'react';
import { downloadFile, fetchJson } from './adminApi';
import { BIDDING_TAG, UPCOMING_TAG, fmt, kr, smallImage, typeLabel } from './adminFormat';
import { getListingKey } from '../map/listingDetailsApi.js';

const PAGE_SIZE = 50;

// The analytics key for a listing row ("bostad/123" / "annons/123"), matching the listing_id sent with every
// listing event — so each row's opens / favourites / realtor clicks can be looked up. A leading slash makes
// getListingKey resolve the kind the same way it does for the site's own listings.
const listingKeyForUrl = (listingUrl) => getListingKey({ listingUrl: `/${String(listingUrl || '').replace(/^\/+/, '')}` });
const EMPTY_FILTERS = { q: '', kommun: '', type: '', min_price: '', max_price: '', sort: 'newest' };

const filterQuery = (filters, extra = {}) => {
    const params = new URLSearchParams();
    Object.entries({ ...filters, ...extra }).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) params.set(key, value);
    });
    return params.toString();
};

export const StatusTag = ({ tag }) => {
    if (!tag) return null;
    const kind = tag === UPCOMING_TAG ? 'upcoming' : tag === BIDDING_TAG ? 'bidding' : '';
    return <span className={`ax-tag ${kind ? `ax-tag--${kind}` : ''}`}>{tag}</span>;
};

const ListingsTab = ({ onOpenDetail, refreshKey }) => {
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [query, setQuery] = useState(EMPTY_FILTERS);
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [kommuner, setKommuner] = useState([]);
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState(false);
    const [stats, setStats] = useState({});

    useEffect(() => {
        fetchJson('/admin/explorer/kommuner').then((list) => setKommuner(Array.isArray(list) ? list : [])).catch(() => {});
    }, [refreshKey]);

    // Per-listing engagement (opens, favourites, realtor clicks), keyed by listing_id, for all of time.
    useEffect(() => {
        let active = true;
        fetchJson('/admin/analytics/listings')
            .then((payload) => active && setStats(payload || {}))
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [refreshKey]);

    // Typing settles for 300 ms before the table reloads.
    useEffect(() => {
        const timer = window.setTimeout(() => {
            setQuery(filters);
            setPage(1);
        }, 300);
        return () => window.clearTimeout(timer);
    }, [filters]);

    useEffect(() => {
        let active = true;
        setError('');
        fetchJson(`/admin/explorer/listings?${filterQuery(query, { page, page_size: PAGE_SIZE })}`)
            .then((payload) => active && setData(payload))
            .catch((err) => active && setError(err.message));
        return () => {
            active = false;
        };
    }, [query, page, refreshKey]);

    const setField = (event) => setFilters((prev) => ({ ...prev, [event.target.name]: event.target.value }));
    const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

    const handleDownload = async () => {
        setDownloading(true);
        try {
            await downloadFile(`/admin/explorer/listings.csv?${filterQuery(query, { sort: '' })}`, 'active_listings.csv');
        } catch (err) {
            setError(err.message);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <>
            <form className="ax-filters" onSubmit={(event) => event.preventDefault()}>
                <input type="search" name="q" value={filters.q} onChange={setField} placeholder="Search address, area, kommun…" aria-label="Search" />
                <select name="kommun" value={filters.kommun} onChange={setField} aria-label="Kommun">
                    <option value="">All kommuner</option>
                    {kommuner.map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
                <select name="type" value={filters.type} onChange={setField} aria-label="Type">
                    <option value="">All types</option>
                    <option value="apartment">Apartment</option>
                    <option value="house">House</option>
                    <option value="house++">Townhouse / semi</option>
                </select>
                <input type="number" name="min_price" value={filters.min_price} onChange={setField} placeholder="Min price" min="0" step="100000" aria-label="Min price" />
                <input type="number" name="max_price" value={filters.max_price} onChange={setField} placeholder="Max price" min="0" step="100000" aria-label="Max price" />
                <select name="sort" value={filters.sort} onChange={setField} aria-label="Sort">
                    <option value="newest">Newest</option>
                    <option value="price_asc">Price ↑</option>
                    <option value="price_desc">Price ↓</option>
                    <option value="sqm_asc">kr/m² ↑</option>
                    <option value="sqm_desc">kr/m² ↓</option>
                    <option value="area_desc">Largest</option>
                </select>
                <button type="button" className="ax-button ax-button--secondary" onClick={handleDownload} disabled={downloading}>
                    {downloading ? 'Preparing…' : 'Download CSV'}
                </button>
            </form>
            {error && <p className="ax-error">{error}</p>}
            <section className="ax-card ax-table-card">
                <div className="ax-table-meta">
                    <span>{data ? `${fmt(data.total)} listings` : 'Loading…'}</span>
                    <span className="ax-pager">
                        <button type="button" onClick={() => setPage(page - 1)} disabled={page <= 1}>‹ Prev</button>
                        <span>page {fmt(page)} / {fmt(pages)}</span>
                        <button type="button" onClick={() => setPage(page + 1)} disabled={page >= pages}>Next ›</button>
                    </span>
                </div>
                <div className="ax-table-scroll">
                    <table className="ax-table ax-listings">
                        <thead>
                            <tr>
                                <th aria-label="Photo" /><th>Address</th><th>Type</th><th className="num">Price</th>
                                <th className="num">kr/m²</th><th className="num">Rooms</th><th className="num">m²</th>
                                <th className="num">Fee</th><th className="num">Built</th><th>Tenure</th><th>Status</th>
                                <th className="num" title="Times the listing was opened">Opens</th>
                                <th className="num" title="Times saved to favourites">Favs</th>
                                <th className="num" title="Click-throughs to the realtor">Realtor</th>
                                <th>Links</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data && data.rows.length === 0 && (
                                <tr><td colSpan={15}>No listings match.</td></tr>
                            )}
                            {data && data.rows.map((row) => {
                              const s = stats[listingKeyForUrl(row.listingUrl)];
                              return (
                                <tr key={row.listingUrl} onClick={(event) => !event.target.closest('a') && onOpenDetail(row.listingUrl)} title="Show all images and details">
                                    <td>{row.thumb ? <img loading="lazy" alt="" src={smallImage(row.thumb)} /> : <span className="ax-thumb-empty" />}</td>
                                    <td className="ax-addr">
                                        <button type="button" className="ax-row-link" onClick={() => onOpenDetail(row.listingUrl)}>{row.address || row.listingUrl}</button>
                                        <small>
                                            {[row.ort, row.kommun].filter(Boolean).join(', ')}
                                            {row.housingCoopName ? ` · ${row.housingCoopName}` : ''}
                                        </small>
                                    </td>
                                    <td>{typeLabel(row.type)}</td>
                                    <td className="num">{kr(row.price)}</td>
                                    <td className="num">{row.sqmPrice ? fmt(row.sqmPrice) : '—'}</td>
                                    <td className="num">{fmt(row.rooms || '')}</td>
                                    <td className="num">{fmt(row.livingM2 || '')}</td>
                                    <td className="num">{row.fee ? fmt(row.fee) : '—'}</td>
                                    <td className="num">{row.constructionYear || '—'}</td>
                                    <td>{row.tenureForm || '—'}</td>
                                    <td><StatusTag tag={row.imgTag} /></td>
                                    <td className="num">{s ? fmt(s.opens) : '—'}</td>
                                    <td className="num">{s ? fmt(s.favorites) : '—'}</td>
                                    <td className="num">{s ? fmt(s.realtor) : '—'}</td>
                                    <td>
                                        {row.realtorUrl && (
                                            <a href={row.realtorUrl} target="_blank" rel="noopener noreferrer">Realtor</a>
                                        )}
                                    </td>
                                </tr>
                              );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>
        </>
    );
};

export default ListingsTab;
